<script setup lang="ts">
import { useRoute } from "vue-router";
import { ref, onMounted, onUnmounted } from "vue";
import type { Ref } from "vue";
import GuestCheckoutDetailsForm from "@/components/4-Checkout/GuestCheckoutDetailsForm.vue";
import type { GuestUserDataModel } from "@/models/checkout/UserDataModel";
import { fetchAuthSession } from "aws-amplify/auth";
import type { IBookingData } from "@/models/checkout/FulfilmentRequestModel";
import StripeWidget from "@/components/4-Checkout/StripeWidget.vue";
import UserPayment from "@/components/4-Checkout/UserPayment.vue";
import { usePaymentStore } from "@/stores/payment";
import { Hub } from "aws-amplify/utils";
import CheckoutLhs from "@/components/4-Checkout/CheckoutLhs.vue";
import emitter from "@/eventBus";
import PaymentConfirmation from "./PaymentConfirmation.vue";

// When true shows the payment screen, when false shows the screen to enter guest details.
const togglePayment: Ref<boolean> = ref(false);
const paymentConfirmed: Ref<boolean> = ref(false);
const isLoading: Ref<boolean> = ref(false);
const userEmail: Ref<string> = ref("");
const route = useRoute();
const loadingMessage: Ref<string> = ref("Please wait, bookings can take up to 20 seconds");
const bookingData: Ref<IBookingData | null> = ref(null);
const isAuthUser: Ref<boolean> = ref(false);
const paymentStore = usePaymentStore();
async function resyncAuthUser() {
    const session = await fetchAuthSession();
    if (session.tokens !== undefined) {
        // if we're logged in
        isAuthUser.value = true;
        togglePayment.value = true;
        userEmail.value = session.tokens.signInDetails!.loginId!;
    } else {
        isAuthUser.value = false;
        togglePayment.value = false;
    }
}

Hub.listen("auth", () => {
    resyncAuthUser();
});

function toggleLoading(loadingStatus: boolean) {
    isLoading.value = loadingStatus;
}

// Called when the guest user has entered their details and clicked the button to proceed to payment. This will then set togglePayment to true, which will show the payment screen.
function setGuestUserData(userData: GuestUserDataModel) {
    paymentStore.guestUserFirstName = userData.firstName;
    paymentStore.guestUserLastName = userData.surname;
    paymentStore.guestUserEmail = userData.userEmail;
    togglePayment.value = true;
}

onMounted(async () => {
    await resyncAuthUser();
    paymentStore.stripePaymentErrorToDisplay = null;
});

const bookingId = route.query.bookingId as string;
const fulfilmentMethod = route.query.deliveryMethod as string;

// When the payment function in the payment store has successfully completed the payment, set variables to move the screen on. The bookingData payload is used to display the booking confirmation page.
function handlePaymentSuccess(bookingDataPayload: IBookingData | null) {
    paymentConfirmed.value = true;
    togglePayment.value = false;
    bookingData.value = bookingDataPayload;
}

onMounted(() => {
    emitter.on("paymentCompletedSuccessfully", (eventPayload: any) => handlePaymentSuccess(eventPayload));
});

onUnmounted(() => {
    emitter.off("paymentCompletedSuccessfully", (eventPayload: any) => handlePaymentSuccess(eventPayload));
});
</script>

<template>
    <div>
        <v-overlay :model-value="paymentStore.isFinalPaymentStepLoading" :content-class="'justify-center align-center d-flex flex-column w-100 h-100'">
            <v-progress-circular indeterminate :size="70" :width="7" color="primary"> </v-progress-circular>
            <h2>{{ loadingMessage }}</h2>
        </v-overlay>

        <!-- This v-container is for guest checkout -->
        <v-container class="pa-0 pa-md-2 pb-5">
            <v-row>
                <v-col class="col-sm" v-if="!isAuthUser && !paymentConfirmed">
                    <CheckoutLhs></CheckoutLhs>
                </v-col>
                <v-col class="col-sm guest-checkout" v-if="!togglePayment && !paymentConfirmed && !isAuthUser">
                    <div>
                        <div class="pt-10">
                            <GuestCheckoutDetailsForm @isLoading="toggleLoading($event)" @guestUserData="setGuestUserData($event)"></GuestCheckoutDetailsForm>
                        </div>
                    </div>
                </v-col>
                <v-col class="col-sm ml-sm-5 guest-checkout" v-if="togglePayment && !isAuthUser">
                    <h2>Guest Checkout</h2>
                    <div class="pt-10">
                        <StripeWidget :booking-id="bookingId" :fulfilmentMethod="fulfilmentMethod"></StripeWidget>
                    </div>
                    <div class="pt-10"></div>
                </v-col>
            </v-row>
        </v-container>

        <!-- This v-container is for authenticated user (B2B/C) -->
        <UserPayment v-if="isAuthUser && togglePayment" :booking-id="bookingId" :fulfilmentMethod="fulfilmentMethod"></UserPayment>

        <!-- Display the final order confirmed page once order is successful -->
        <div v-if="paymentConfirmed">
            <PaymentConfirmation :bookingData="bookingData!.booking"></PaymentConfirmation>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.v-container {
    max-width: 1200px;
}
.v-overlay {
    background-color: rgba(255, 255, 255, 0.9);
    min-height: 70vh;
}

.v-overlay :deep(.v-overlay__scrim) {
    background: none;
}

@media (min-width: 760px) {
    .v-col.col-sm.guest-checkout {
        border-top: 3px solid var(--vt-c-orange);
    }
}

.cost-summary {
    font-weight: 500;

    .primary-button {
        width: 100%;
        text-align: center;
        font-size: 17px;
        font-weight: 500;

        &.invalid {
            pointer-events: none;
            background-color: var(--vt-c-disabled-button);
        }
    }

    .accept-conditions {
        display: inline-flex;
        line-height: 4;
    }

    h3 {
        color: var(--vt-c-deep-red);
        font-weight: 500;
    }
}

h2.title {
    color: var(--vt-c-deep-gray);
    font-size: 2rem;
    font-weight: 600;
    line-height: 1.125;
    padding-bottom: 0.625rem;
}

.v-col.col-sm.guest-checkout {
    flex: none;
    width: 50%;
}

:deep(.v-overlay__content) {
    text-align: center;
    backdrop-filter: blur(2px);
}

@media only screen and (max-width: 760px) {
    .v-col.col-sm.guest-checkout {
        width: 100%;
    }
}
</style>
